import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  useCreateAmendmentV2,
  useCreatePayInstructionV2,
  useCreateTerminationV2,
} from '@xemplo/amendment-query';
import { ButtonIconPosition, ButtonType } from '@xemplo/button';
import { AmendmentTypes, ChangeTaskTypes } from '@xemplo/gp-types';
import { DawnArrowLongLeft } from '@xemplo/icons';
import { safeCallAsync } from '@xemplo/safe-call';
import { ToastType, useToast } from '@xemplo/toast';

import { FormFields } from '../create-amendment.types';

import {
  AmountMethodOptions,
  ParsedCsvItem,
} from './form/page-2/bulk-upload/bulk-upload.types';
import { handlePayInstruction } from './form/page-2/pay-instruction';
import { StepOne } from './form/step-one';
import { StepThree } from './form/step-three';
import { StepTwo } from './form/step-two';
import {
  preparePayload,
  prepareTerminationPayload,
} from './create-amendment-form.helper';
import { ModalBodyProps } from './create-amendment-form.types';

export const useCreateAmendmentForm = (props: Readonly<ModalBodyProps>) => {
  const { onRequestClose, onClose, onSuccess, payrunModeValues, selectedWorkers } = props;
  const navigate = useNavigate();

  const payrunMode = !!payrunModeValues?.payrunId && !!payrunModeValues?.businessUnitId;

  function buildDefaultValues() {
    return {
      changeType: payrunModeValues?.payrunId ? AmendmentTypes.PAYRUN : undefined,
      payrun: payrunModeValues?.payrunId ?? undefined,
      businessUnit: payrunModeValues?.businessUnitId ?? undefined,
      appliesTo: selectedWorkers ?? [],
    };
  }

  const methods = useForm<FormFields>({
    defaultValues: buildDefaultValues(),
  });

  const [step, setStep] = useState(payrunMode ? 2 : 1);
  const [csvData, setCsvData] = useState<ParsedCsvItem[]>([]);
  const [csvDataWarnings, setCsvDataWarnings] = useState<string[]>([]);
  const { addToast } = useToast();
  const watchAmountMethod = methods.watch('amountMethod');
  const watchChangeTask = methods.watch('changeTask');

  function handleSuccessfulCreate(type: string) {
    watchChangeTask !== ChangeTaskTypes.TERMINATION && onSuccess?.();
    addToast({
      text: `${type} created`,
      type: ToastType.Confirmation,
    });
    onClose();
  }

  const { mutateAsync } = useCreateAmendmentV2();
  const { mutateAsync: createPayInstruction } = useCreatePayInstructionV2();
  const { mutateAsync: createTerminationAsync } = useCreateTerminationV2();

  const handleNextStep = async () => {
    const isValid = await methods.trigger();
    if (isValid) {
      setStep((prev) => prev + 1);
    }
  };

  const handleSubmit = methods.handleSubmit(async (data) => {
    if (data.changeTask === ChangeTaskTypes.PAY_INSTRUCTION) {
      const [, error] = await safeCallAsync(
        handlePayInstruction({ data, createPayInstruction }),
      );
      if (error) return;
      handleSuccessfulCreate('Pay Instruction');
      return;
    }

    if (data.changeTask === ChangeTaskTypes.TERMINATION) {
      const [response, error] = await safeCallAsync(
        createTerminationAsync(prepareTerminationPayload(data)),
      );
      if (error) return;
      handleSuccessfulCreate('Termination');
      navigate(`/payrun/amendment/${response.result[0]}`);
      return;
    }

    const [, error] = await safeCallAsync(mutateAsync(preparePayload(data, csvData)));
    if (error) return;
    handleSuccessfulCreate('Amendment');
  });

  function getPrimaryButton() {
    switch (step) {
      case 1:
        return {
          onClick: handleNextStep,
          label: 'Next',
          ariaLabel: 'Next page button',
          type: ButtonType.Primary,
        };
      case 2:
        return watchAmountMethod === AmountMethodOptions.Variable
          ? {
              onClickAsync: handleNextStep,
              disabled: csvData.length === 0,
              label: 'Next',
              ariaLabel: 'Next page button',
              type: ButtonType.Primary,
            }
          : {
              onClickAsync: handleSubmit,
              label: 'Submit',
              ariaLabel: 'Submit',
              type: ButtonType.Primary,
            };
      case 3:
        return {
          onClickAsync: handleSubmit,
          label: 'Submit',
          ariaLabel: 'Submit',
          type: ButtonType.Primary,
        };

      default:
        return undefined;
    }
  }

  function getTertiaryButton() {
    switch (step) {
      case 1:
        return {
          onClick: () => onRequestClose(),
          label: 'Cancel',
          ariaLabel: 'Cancel',
          type: ButtonType.Tertiary,
        };
      case 2:
        if (payrunMode) {
          return {
            onClick: () => onRequestClose(),
            label: 'Cancel',
            ariaLabel: 'Cancel',
            type: ButtonType.Tertiary,
          };
        }
        return {
          onClick: () => {
            setStep((prev) => prev - 1);
            methods.reset(buildDefaultValues());
          },
          label: 'Back',
          ariaLabel: 'Back',
          type: ButtonType.Tertiary,
          icon: <DawnArrowLongLeft fontSize={16} />,
          iconPosition: ButtonIconPosition.Leading,
        };
      case 3:
        return {
          onClick: () => {
            setStep((prev) => prev - 1);
          },
          label: 'Back',
          ariaLabel: 'Back',
          type: ButtonType.Tertiary,
          icon: <DawnArrowLongLeft fontSize={16} />,
          iconPosition: ButtonIconPosition.Leading,
        };

      default:
        return undefined;
    }
  }

  function resetForm() {
    methods.reset(buildDefaultValues());
    setCsvData([]);
    setCsvDataWarnings([]);
    setStep(payrunMode ? 2 : 1);
  }

  const primaryButton = getPrimaryButton();
  const tertiaryButton = getTertiaryButton();

  function getFormStep() {
    switch (step) {
      case 1:
        return <StepOne />;
      case 2:
        return (
          <StepTwo
            setCsvData={setCsvData}
            setCsvDataWarnings={setCsvDataWarnings}
            selectedWorkers={selectedWorkers}
          />
        );
      case 3:
        return <StepThree csvData={csvData} csvDataWarnings={csvDataWarnings} />;
      default:
        return null;
    }
  }

  return {
    methods,
    getFormStep,
    primaryButton,
    tertiaryButton,
    resetForm,
  };
};
